/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * DynamicHeader — header/nav for custom Page Builder homepages.
 *
 * When page_config.header contains the full MenuConfig (has an 'items' array),
 * delegates entirely to NavMenuSection which handles positioning, scroll spy,
 * frosted glass, mobile drawer, etc.
 *
 * Falls back to a plain Bootstrap sticky nav for pages still using the old
 * simple format (logo_url / nav_links / show_sign_in).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import envConfig from '../config/envConfig';
import Button from './Button';
import NavMenuSection from './dynamic-page/sections/NavMenuSection';

export default function DynamicHeader({ headerConfig, appBranding }) {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const cfg = headerConfig || {};

  // ── New format: full MenuConfig from MenuNavPanel ─────────────────────────
  if (Array.isArray(cfg.items)) {
    return <NavMenuSection config={cfg} appBranding={appBranding} />;
  }

  // ── Legacy format: simple header (logo_url, nav_links, auth buttons) ─────
  const branding  = appBranding || {};
  const logoUrl   = cfg.logo_url || branding.logo || '';
  const appName   = cfg.app_name || branding.app_name || '';
  const navLinks  = Array.isArray(cfg.nav_links) ? cfg.nav_links : [];
  const showSignIn = cfg.show_sign_in !== false && envConfig.isUserLoginAllowed;
  const showSignUp = cfg.show_sign_up !== false && envConfig.isUserRegistrationAllowed;

  const hasLogo = logoUrl && logoUrl.toLowerCase() !== 'default';

  const handleNavClick = (link) => {
    if (!link.url) return;
    if (link.target === '_blank' || link.url.startsWith('http')) {
      window.open(link.url, link.target || '_blank', 'noopener noreferrer');
    } else if (link.url.startsWith('#')) {
      const el = document.getElementById(link.url.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(link.url);
    }
  };

  return (
    <nav className="navbar navbar-expand-lg navbar-light py-3"
      style={{ background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', position: 'sticky', top: 0, zIndex: 1000 }}>
      <div className="container-fluid px-4">
        <a className="navbar-brand d-flex align-items-center gap-2" href="/"
          style={{ color: theme.primary, fontWeight: 800, fontSize: '1.4rem', textDecoration: 'none' }}>
          {hasLogo && (
            <img src={logoUrl} alt={appName || 'Logo'}
              style={{ height: 40, objectFit: 'contain' }}
              onError={e => { e.currentTarget.style.display = 'none'; }} />
          )}
          {appName && <span style={{ fontWeight: 800 }}>{appName}</span>}
        </a>

        <button className="navbar-toggler" type="button"
          data-bs-toggle="collapse" data-bs-target="#dynamicHeaderNav"
          aria-controls="dynamicHeaderNav" aria-expanded="false" aria-label="Toggle navigation"
          style={{ border: `2px solid ${theme.primary}` }}>
          <span className="navbar-toggler-icon" />
        </button>

        <div className="collapse navbar-collapse" id="dynamicHeaderNav">
          {navLinks.length > 0 && (
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0">
              {navLinks.map((link, idx) => (
                <li key={idx} className="nav-item">
                  <button className="btn btn-link nav-link" onClick={() => handleNavClick(link)}
                    style={{ color: '#374151', fontWeight: 500, textDecoration: 'none' }}>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {(showSignIn || showSignUp) && (
            <div className="d-flex align-items-center gap-2 ms-auto">
              {showSignIn && (
                <Button mode="primary" className="w-lg-auto" onClick={() => navigate(cfg.sign_in_url || '/sign-in')}>
                  {cfg.sign_in_label || 'Sign In'}
                </Button>
              )}
              {showSignUp && (
                <Button mode="bordered" className="w-lg-auto" onClick={() => navigate(cfg.sign_up_url || '/sign-up')}>
                  {cfg.sign_up_label || 'Sign Up'}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
